import React from "react";
import { ClientRowData, ClientAdjustmentRecord } from "./types";
import { parseNum } from "./utils";

interface AdjustmentTableProps {
    isLoading: boolean;
    filteredClients: ClientRowData[];
    allSelected: boolean;
    handleToggleSelectAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
    selectedMap: Record<number, boolean>;
    handleToggleSelectRow: (clientId: number) => void;
    savedAdjustments: Record<number, ClientAdjustmentRecord>;
    drafts: Record<number, string>;
    cycleYear: number;
    isPastYear: boolean;
    focusedInput: number | 'bulk' | null;
    handleDraftInput: (clientId: number, value: string) => void;
    handleDraftFocus: (clientId: number, e: React.FocusEvent<HTMLInputElement>) => void;
    handleDraftBlur: (clientId: number) => void;
    handleOpenHistory: (client: ClientRowData) => void;
    handleOpenRemove: (client: ClientRowData) => void;
}

export const AdjustmentTable: React.FC<AdjustmentTableProps> = ({
    isLoading,
    filteredClients,
    allSelected,
    handleToggleSelectAll,
    selectedMap,
    handleToggleSelectRow,
    savedAdjustments,
    drafts,
    cycleYear,
    isPastYear,
    focusedInput,
    handleDraftInput,
    handleDraftFocus,
    handleDraftBlur,
    handleOpenHistory,
    handleOpenRemove,
}) => {
    return (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden" }}>
            {/* Header da Tabela */}
            <div style={{ display: "grid", gridTemplateColumns: "42px 1.4fr 1fr 130px 150px 150px 84px", alignItems: "center", gap: "12px", padding: "13px 18px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb", fontSize: "11.5px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".03em" }}>
                <div>
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleToggleSelectAll}
                        style={{ width: "16px", height: "16px", accentColor: "#0d9488", cursor: "pointer" }}
                    />
                </div>
                <div>Cliente</div>
                <div>Localização / Grupo</div>
                <div>Reajuste atual</div>
                <div>Novo reajuste (%)</div>
                <div>Ciclo anterior</div>
                <div style={{ textAlign: "right" }}>Ações</div>
            </div>

            {/* Linhas de Clientes */}
            {isLoading ? (
                <div style={{ padding: "48px", textAlign: "center", color: "#64748b", fontSize: "14px" }}>
                    Carregando clientes e reajustes...
                </div>
            ) : filteredClients.length === 0 ? (
                <div style={{ padding: "48px", textAlign: "center", color: "#94a3b8", fontSize: "13.5px" }}>
                    Nenhum cliente encontrado para os filtros aplicados.
                </div>
            ) : (
                filteredClients.map((client) => {
                    const isSelected = !!selectedMap[client.clientId];
                    const saved = savedAdjustments[client.clientId];
                    const draftStr = drafts[client.clientId] ?? "";

                    const draftNum = parseNum(draftStr);
                    const isInvalid = !isNaN(draftNum) && (draftNum < 0 || draftNum > 100);

                    const savedVal = saved ? saved.adjustment : null;
                    const isModified =
                        !isPastYear &&
                        draftStr !== "" &&
                        !isNaN(draftNum) &&
                        (savedVal === null || Number(savedVal.toFixed(2)) !== Number(draftNum.toFixed(2)));

                    const rowBg = isModified ? "#fffbeb" : "#fff";

                    const prevYear = cycleYear - 1;
                    const prevAdjVal = client.adjustments ? client.adjustments[prevYear] : undefined;
                    const prevCycleStr = prevAdjVal !== undefined ? `${prevAdjVal.toFixed(2).replace(".", ",")}% (${prevYear})` : "—";

                    return (
                        <div
                            key={client.clientId}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "42px 1.4fr 1fr 130px 150px 150px 84px",
                                alignItems: "center",
                                gap: "12px",
                                padding: "14px 18px",
                                borderBottom: "1px solid #f1f5f9",
                                background: rowBg,
                            }}
                        >
                            {/* Checkbox */}
                            <div>
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggleSelectRow(client.clientId)}
                                    disabled={isPastYear}
                                    style={{ width: "16px", height: "16px", accentColor: "#0d9488", cursor: isPastYear ? "not-allowed" : "pointer", opacity: isPastYear ? 0.35 : 1 }}
                                />
                            </div>

                            {/* Nome do Cliente e CNX */}
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1e293b" }}>
                                        {client.clientFantasyName}
                                    </span>
                                    <span style={{ fontSize: "10.5px", fontWeight: 600, color: "#475569", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "2px 8px" }}>
                                        {client.clientCodeCnx || "CNX"}
                                    </span>
                                </div>
                                <div style={{ fontSize: "11.5px", color: "#94a3b8", marginTop: "3px" }}>
                                    {client.clientCompanyName} · {client.clientCnpj || "-"}
                                </div>
                            </div>

                            {/* Cidade, UF e Grupo */}
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: "12.5px", color: "#334155" }}>
                                    {client.clientCity || "-"} — {client.clientState || "-"}
                                </div>
                                {client.clientCustomerGroup && (
                                    <span style={{ fontSize: "10.5px", fontWeight: 600, color: "#1d4ed8", background: "#eff6ff", borderRadius: "20px", padding: "2px 8px", display: "inline-block", marginTop: "3px" }}>
                                        {client.clientCustomerGroup}
                                    </span>
                                )}
                            </div>

                            {/* Reajuste Atual (Vigente) */}
                            <div>
                                {saved ? (
                                    <span style={{ fontSize: "13px", fontWeight: 700, color: "#0f766e", background: "#f0fdfa", border: "1px solid #ccfbf1", borderRadius: "8px", padding: "5px 10px" }}>
                                        {saved.adjustment.toFixed(2).replace(".", ",")}%
                                    </span>
                                ) : (
                                    <span style={{ fontSize: "12px", color: "#94a3b8" }}>— sem reajuste</span>
                                )}
                            </div>

                            {/* Novo Reajuste (%) */}
                            <div>
                                {isPastYear ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                        {saved ? (
                                            <span style={{
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: "#94a3b8",
                                                background: "#f8fafc",
                                                border: "1.5px solid #e2e8f0",
                                                borderRadius: "8px",
                                                padding: "6px 12px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "3px",
                                            }}>
                                                {saved.adjustment.toFixed(2).replace(".", ",")}<span style={{ fontSize: "10px", opacity: 0.6 }}>%</span>
                                            </span>
                                        ) : (
                                            <span style={{ fontSize: "12px", color: "#cbd5e1" }}>—</span>
                                        )}
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" title="Ano passado — somente leitura">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                        </svg>
                                    </div>
                                ) : (
                                    <div style={{ position: "relative", display: "inline-block" }}>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            placeholder="0,00"
                                            value={draftStr}
                                            onChange={(e) => handleDraftInput(client.clientId, e.target.value)}
                                            onFocus={(e) => handleDraftFocus(client.clientId, e)}
                                            onBlur={() => handleDraftBlur(client.clientId)}
                                            style={{
                                                width: "90px",
                                                padding: "7px 26px 7px 9px",
                                                border: `1.5px solid ${isInvalid
                                                    ? "#ef4444"
                                                    : focusedInput === client.clientId
                                                        ? "#0d9488"
                                                        : isModified
                                                            ? "#f59e0b"
                                                            : draftStr
                                                                ? "#cbd5e1"
                                                                : "#e2e8f0"
                                                    }`,
                                                borderRadius: "8px",
                                                fontSize: "13.5px",
                                                fontWeight: isModified ? 700 : 500,
                                                background: isInvalid
                                                    ? "#fef2f2"
                                                    : focusedInput === client.clientId
                                                        ? "#f0fdfa"
                                                        : isModified
                                                            ? "#fffbeb"
                                                            : "#fff",
                                                outline: "none",
                                                textAlign: "right",
                                                transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
                                                boxShadow: focusedInput === client.clientId
                                                    ? "0 0 0 3px rgba(13,148,136,0.15)"
                                                    : isInvalid
                                                        ? "0 0 0 3px rgba(239,68,68,0.12)"
                                                        : isModified
                                                            ? "0 0 0 2px rgba(245,158,11,0.10)"
                                                            : "none",
                                                color: isInvalid ? "#dc2626" : isModified ? "#92400e" : "#1e293b",
                                                cursor: "text",
                                            }}
                                        />
                                        <span style={{
                                            position: "absolute",
                                            right: "8px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            fontSize: "11px",
                                            fontWeight: 700,
                                            color: isInvalid
                                                ? "#ef4444"
                                                : isModified
                                                    ? "#d97706"
                                                    : "#94a3b8",
                                            pointerEvents: "none",
                                            userSelect: "none",
                                        }}>%</span>
                                    </div>
                                )}
                                {isInvalid && (
                                    <div style={{ fontSize: "10px", color: "#dc2626", marginTop: "3px", fontWeight: 600 }}>
                                        0,00 – 100,00
                                    </div>
                                )}
                                {isModified && !isInvalid && draftStr && (
                                    <div style={{ fontSize: "10px", color: "#92400e", marginTop: "3px", fontWeight: 600, display: "flex", alignItems: "center", gap: "3px" }}>
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                        Não salvo
                                    </div>
                                )}
                            </div>

                            {/* Ciclo Anterior */}
                            <div style={{ fontSize: "12.5px", color: "#64748b" }}>
                                {prevCycleStr}
                            </div>

                            {/* Botões de Ações */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}>
                                <button
                                    onClick={() => handleOpenHistory(client)}
                                    title="Histórico"
                                    style={{ width: "30px", height: "30px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                </button>

                                {saved && !isPastYear && (
                                    <button
                                        onClick={() => handleOpenRemove(client)}
                                        title="Remover reajuste do ciclo"
                                        style={{ width: "30px", height: "30px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
};
